import { UseFormReturn } from "react-hook-form/dist/types/form";
import { getStatusColor } from "../../utils/helpers";
import toast from "react-hot-toast";
import { useAuth } from "../../../../../../public/auth/infrastructure/store/auth-context";
import { useOffers } from "../../../store";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import {
  Button,
  Form,
  Input,
  LoadingIcon,
  LockIcon,
  SelectMenu,
  TriangleIcon,
  WarningIcon,
} from "../../../../../../shared/infrastructure";
import { OfferT, StatusList } from "../../../../domain";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { number, object, string } from "yup";
import {
  offerKeys,
  useBlockOffer,
  useLowerRateOffer,
  useUnblockOffer,
  useUpdateOfferStatus,
} from "../../../services";

type FormContentProps = {
  isFormLoading: boolean;
  control: UseFormReturn<OfferStatusUpdateFormType>["control"];
  close: () => void;
  offer: OfferT;
  offerIsBlocked: boolean;
  initialStatus?: ApprovalsStatus;
};

const FormContent = ({
  isFormLoading,
  control,
  close,
  offer,
  offerIsBlocked,
  initialStatus,
}: FormContentProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const status = useWatch({
    control,
    name: "status",
    defaultValue: initialStatus,
  });
  const counter_offer = useWatch({
    control,
    name: "counter_offer",
  }) as number;
  const { data, isFetching } = useLowerRateOffer(offer.approval_id);

  const acceptButtonStatus = useMemo(() => {
    if (isFormLoading) return "loading";
    else if (
      status === undefined ||
      (status === "counter" && counter_offer === undefined) ||
      isFetching
    ) {
      return "disabled";
    }

    return "default";
  }, [counter_offer, isFetching, isFormLoading, status]);

  return (
    <React.Fragment>
      <section className="grid grid-cols-[auto_1fr] items-start gap-[20px]">
        <span className="text-[12px] font-[500] text-[#000000]">
          {t<string>(
            "context.private.components.offer-status-update.select-status"
          )}
          :
        </span>
        <section className="grid justify-start gap-[20px]">
          <SelectMenu<string, typeof StatusList[0]>
            name="status"
            items={StatusList}
            disabled={offerIsBlocked}
            defaultValue={status || "Status"}
            renderItem={({ value }) => (
              <section className="grid grid-flow-col items-center gap-[10px]">
                <span
                  className={`h-[10px] w-[10px] rounded-full ${getStatusColor(
                    value,
                    "bg"
                  )}`}
                />
                <span
                  className={`text-[12px] capitalize ${getStatusColor(
                    value,
                    "text"
                  )}]`}
                >
                  {value}
                </span>
              </section>
            )}
          >
            {({ value }) => (
              <section
                className={`grid grid-cols-[14px_auto_14px] items-center justify-center gap-[5px] rounded-[5px] border-[1px] border-solid px-[12px] py-[4px] text-[12px] font-[500] ${getStatusColor(
                  value,
                  "text"
                )} border-current capitalize`}
              >
                <span
                  className={`h-[10px] w-[10px] rounded-full ${getStatusColor(
                    value,
                    "bg"
                  )}`}
                />
                <span>{value}</span>
                {offerIsBlocked ? (
                  <LockIcon width={14} height={14} fill="#919191" />
                ) : (
                  <TriangleIcon />
                )}
              </section>
            )}
          </SelectMenu>
          {status === "counter" && (
            <section className="grid grid-flow-col content-start gap-[4px]">
              <span className="text-[12px] font-[500] text-[#919191]">$</span>
              <Input
                name="counter_offer"
                placeholder="Rate"
                type="text"
                variant="standard"
                rootInputProps={{
                  className: "py-1.5 text-[12px] text-center w-[100px]",
                }}
              />
              <span className="text-[12px] font-[500] text-[#919191]">USD</span>
            </section>
          )}
        </section>
      </section>
      {offerIsBlocked ? (
        <section>
          <span className="text-[10px] text-[#2196F3]">
            {offer.blocking_approval_user}{" "}
            {t<string>(
              "context.private.components.offer-status-update.blocked"
            )}
          </span>
        </section>
      ) : null}
      <section className="grid gap-[10px]">
        <span className="text-[12px] font-[500] text-[#000000]">
          {t<string>("context.private.components.offer-status-update.comments")}
          :
        </span>
        <Input
          name="status_comment"
          placeholder="Insert comment here"
          type="text"
          variant="standard"
          multiline={true}
          rows={3}
          inputProps={{ disableUnderline: true }}
          rootInputProps={{
            className: "text-[12px]",
          }}
        />
      </section>
      {offer.creation_source === "CP" && (
        <section className="grid grid-flow-col items-center justify-center gap-[8px] px-[20px] pb-[20px] text-[12px] text-[#919191]">
          <WarningIcon fill="#F44336" />
          <span>
            This Load has been created from the Capacity Portal.
            <br />
            Therefore it won’t be booked until the carrier confirms.
          </span>
        </section>
      )}
      {status === "approved" && isFetching && (
        <section className="grid grid-flow-col items-center justify-center gap-[8px] px-[20px] pb-[20px] text-[12px] text-[#919191]">
          <LoadingIcon fill="#147EFB" />
        </section>
      )}
      {status === "approved" && !isFetching && !data?.data.is_lowest_rate && (
        <section className="grid grid-flow-col items-center justify-center gap-[8px] px-[20px] pb-[20px] text-[12px] text-[#919191]">
          <WarningIcon fill="#F44336" />
          <span>There is a lower offer available</span>
        </section>
      )}
      <section className="grid grid-flow-col justify-end gap-[20px]">
        <Button
          appearance="secondary"
          className="w-[100px]"
          type="button"
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          appearance="primary"
          className="w-[100px]"
          status={acceptButtonStatus}
        >
          Accept
        </Button>
      </section>
    </React.Fragment>
  );
};

type ApprovalsStatus = "approved" | "rejected" | "counter";

type OfferStatusUpdateFormType = {
  status: ApprovalsStatus;
  status_comment: string;
  counter_offer?: number;
};

const OfferStatusUpdateFormSchema = object().shape({
  status: string()
    .required("Please select a status")
    .oneOf(["approved", "rejected", "counter"], "Please select a valid status"),
  status_comment: string(),
  counter_offer: number().when("status", {
    is: "counter",
    then: number()
      .typeError("Must be a number")
      .required("Rate is required")
      .positive("Must be positive")
      .max(99999, "Too high"),
    otherwise: number(),
  }),
});

type OfferStatusUpdateProps = {
  data: OfferT;
  initialStatus?: ApprovalsStatus;
  close: () => void;
};

export const OfferStatusUpdate = ({
  data,
  initialStatus,
  close,
}: OfferStatusUpdateProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
  const { mutateAsync: updateStatus } = useUpdateOfferStatus();
  const { mutateAsync: blockOffer } = useBlockOffer();
  const { mutate: unblockOffer } = useUnblockOffer();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();
  const { user } = useAuth();
  const [username, setUsername] = useState<string | null>(null);
  const [offerIsBlocked, setOfferIsBlocked] = useState(false);
  const [offerMustBeUnblocked, setOfferMustBeUnblocked] = useState(false);

  useEffect(() => {
    if (user?.name && user.name !== username) {
      setUsername(user.name);
    }
  }, [username, user?.name]);

  useEffect(() => {
    const payload = {
      approvalId: data.approval_id,
    };

    const returnUnblockFunction = (): void => {
      offerMustBeUnblocked && unblockOffer(payload);
    };

    if (username === null) {
      // username is null
      return;
    }

    if (data.offer_is_being_blocked && data.blocking_approval_user !== null) {
      // Offer is blocked and user is not null
      if (data.blocking_approval_user !== username) {
        // offer is blocked by other user
        setOfferIsBlocked(true);

        return;
      } else {
        // offer is blocked by me
        setOfferMustBeUnblocked(true);
        setOfferIsBlocked(false);

        return returnUnblockFunction;
      }
    }

    if (!offerMustBeUnblocked) {
      // blocking offer...
      blockOffer(payload)
        .then(() => {
          // offer blocked
          setOfferMustBeUnblocked(true);
        })
        .catch(() => {
          // offer must be blocked so must update data
          toast.error("Something happen blocking the offer");
          updateNewOffers();
          void queryClient.invalidateQueries(offerKeys.queueList());
        });

      return returnUnblockFunction;
    }
  }, [
    blockOffer,
    data.approval_id,
    data.blocking_approval_user,
    data.offer_is_being_blocked,
    offerMustBeUnblocked,
    queryClient,
    unblockOffer,
    updateNewOffers,
    username,
  ]);

  const handleSubmit = useCallback(
    (values: OfferStatusUpdateFormType) => {
      setIsFormLoading(true);
      updateStatus({
        approval_id: data.approval_id,
        status_comment: values.status_comment,
        status: values.status,
        ...(values.status === "counter" && {
          counter_offer: values.counter_offer,
        }),
      })
        .then(() => {
          setIsFormLoading(false);
          toast.success("Offer Status updated successfully");
          close();
        })
        .catch(() => {
          setIsFormLoading(false);
          toast.error("Something happen updating the offer status");
          close();
        });
    },
    [data.approval_id, close, updateStatus]
  );

  return (
    <section className="grid w-[340px] gap-[20px]">
      <section className="grid gap-[4px]">
        <h1 className="text-[20px] font-[700]">
          {t<string>("context.private.components.offer-status-update.title")}
        </h1>
        <span className="text-[10px] font-[400] text-[#919191]">
          {data.type === "single"
            ? `Order No. ${data.order_number}`
            : "Dedicated"}
        </span>
      </section>
      <Form<OfferStatusUpdateFormType>
        onSubmit={handleSubmit}
        schema={OfferStatusUpdateFormSchema}
        className="grid gap-[5px]"
        defaultValues={{ status: initialStatus }}
        mode="onChange"
      >
        {({ control }) => (
          <FormContent
            isFormLoading={isFormLoading}
            control={control}
            close={close}
            offer={data}
            offerIsBlocked={offerIsBlocked}
            initialStatus={initialStatus}
          />
        )}
      </Form>
    </section>
  );
};

export type { ApprovalsStatus };
